import { GetAndSetFootprint } from './calculator';
import { SetUserInfo } from './user';
import { Session } from '@wren/shared';
import { Network } from '@wren/shared';
import * as AB from '../lib/ab';
import * as Sentry from '@sentry/react';

const { setSessionToken } = Session;

// Complete a session once the backend has confirmed the user: store the
// token, hydrate user + footprint, tag Sentry. Shared by the direct login
// paths and the post-2FA verification path.
const completeSession = async (dispatch, responseBody) => {
  AB.reset();
  setSessionToken(responseBody.sessionToken);
  await dispatch(SetUserInfo(responseBody.user));
  dispatch(GetAndSetFootprint());

  Sentry.setUser({
    id: responseBody.user.userId,
    email: responseBody.user.email,
    username: responseBody.user.username,
  });

  return { user: responseBody.user, error: null };
};

// resolves with an error if it happened, null otherwise. When the account
// requires 2FA, resolves with `{ mfaRequired: true, mfaToken }` and does NOT
// establish a session — the caller collects a code and calls VerifyMfaCode.
export const Login = (email, password) => async (dispatch) => {
  const [response, responseBody] = await Network.post('sessions', {
    email,
    password,
  });
  if (response.ok) {
    if (responseBody.mfaRequired) {
      return {
        user: null,
        error: null,
        mfaRequired: true,
        mfaToken: responseBody.mfaToken,
      };
    }
    return completeSession(dispatch, responseBody);
  }
  if (responseBody) {
    return { user: null, error: responseBody.error || responseBody.message };
  }
  return { user: null, error: 'There was an error logging in' };
};

export function LoginWithGoogle(googleAccessToken) {
  return async (dispatch) => {
    const [response, responseBody] = await Network.post('sessions', {
      googleAccessToken,
    });

    if (response.ok) {
      if (responseBody.mfaRequired) {
        return {
          user: null,
          error: null,
          mfaRequired: true,
          mfaToken: responseBody.mfaToken,
        };
      }
      return completeSession(dispatch, responseBody);
    }

    if (responseBody) {
      return { user: null, error: responseBody.error || responseBody.message };
    }
    return { user: null, error: 'There was an error logging in' };
  };
}

// Exchange the pending 2FA token + emailed code for a real session.
export const VerifyMfaCode = (mfaToken, code) => async (dispatch) => {
  const [response, responseBody] = await Network.post('sessions/mfa', {
    mfaToken,
    code,
  });
  if (response.ok) {
    return completeSession(dispatch, responseBody);
  }
  if (responseBody) {
    return { user: null, error: responseBody.error || responseBody.message };
  }
  return { user: null, error: 'There was an error verifying your code' };
};

// Re-send a 2FA code for an in-progress login.
export const ResendMfaCode = (mfaToken) => async () => {
  const [response, responseBody] = await Network.post('sessions/mfa/resend', {
    mfaToken,
  });
  if (response.ok) {
    return { error: null };
  }
  return {
    error:
      (responseBody && (responseBody.error || responseBody.message)) ||
      'There was an error resending your code',
  };
};
